import { useEffect, useMemo, useState } from 'react'
import { ArrowPathIcon } from '@heroicons/react/24/outline'

import { Badge, Button } from '../../components/ui'

import {
  useAvailableConnectors,
  useClearDataSource,
  useDataSourceConfig,
  useSetDataSource,
  useSnapshotStatus,
  useRefreshSnapshot,
  useTestConnection,
} from './api'

export default function ConfigPage() {
  const { data: config, isLoading: configLoading } = useDataSourceConfig()
  const { data: connectors = [], isLoading: connectorLoading } = useAvailableConnectors()
  const setDataSource = useSetDataSource()
  const testConnection = useTestConnection()
  const clearDataSource = useClearDataSource()
  const snapshot = useSnapshotStatus()
  const refreshSnapshot = useRefreshSnapshot()

  const [connectorId, setConnectorId] = useState('')
  const [schema, setSchema] = useState('cashflow')
  const [bootstrapped, setBootstrapped] = useState(false)

  useEffect(() => {
    if (!config || bootstrapped) return
    setConnectorId(config.connector_id ?? '')
    setSchema(config.redshift_schema ?? 'cashflow')
    setBootstrapped(true)
  }, [bootstrapped, config])

  const activeConnector = useMemo(
    () => connectors.find(connector => connector.connector_id === connectorId),
    [connectorId, connectors],
  )

  if (configLoading || connectorLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
          Configuration
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Configure the saved Redshift connector used by Cash Flow V2.
        </p>
      </div>

      <div className="bg-white dark:bg-dark-surface rounded-lg border border-gray-200 dark:border-dark-border p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="font-subtitle text-h3 text-charcoal dark:text-gray-100">
                Presentation-ready data
              </h2>
              <Badge
                variant={
                  snapshot.data?.state === 'ready'
                    ? 'success'
                    : snapshot.data?.state === 'refreshing'
                      ? 'info'
                      : snapshot.data?.state === 'failed_refresh'
                        ? 'warning'
                        : 'secondary'
                }
              >
                {snapshot.data?.state === 'ready'
                  ? 'Ready'
                  : snapshot.data?.state === 'refreshing'
                    ? 'Refreshing'
                    : snapshot.data?.state === 'failed_refresh'
                      ? 'Last good data'
                      : 'Not prepared'}
              </Badge>
            </div>
            <p className="mt-1 text-small text-gray-500 dark:text-gray-400">
              Pages use the last complete snapshot while Redshift refreshes in the background.
            </p>
            {snapshot.data?.published_at ? (
              <p className="mt-2 text-micro text-gray-500 dark:text-gray-400">
                Last published {new Date(snapshot.data.published_at).toLocaleString()}
                {' · '}
                {Object.keys(snapshot.data.dataset_counts ?? {}).length} datasets
              </p>
            ) : null}
            {snapshot.data?.error_message ? (
              <p className="mt-2 text-small text-amber-700 dark:text-amber-400">
                The latest refresh failed. The previous complete snapshot remains active.
              </p>
            ) : null}
          </div>
          <Button
            variant="secondary"
            onClick={() => refreshSnapshot.mutate()}
            disabled={
              !config?.is_connected ||
              refreshSnapshot.isPending ||
              snapshot.data?.state === 'refreshing'
            }
          >
            <ArrowPathIcon
              className={
                refreshSnapshot.isPending || snapshot.data?.state === 'refreshing'
                  ? 'animate-spin'
                  : ''
              }
            />
            {snapshot.data?.state === 'refreshing' ? 'Refreshing…' : 'Refresh snapshot'}
          </Button>
        </div>
        {refreshSnapshot.isError ? (
          <p className="mt-3 text-small text-red-600 dark:text-red-400">
            {refreshSnapshot.error.message || 'Could not queue the refresh.'}
          </p>
        ) : null}
      </div>

      <div className="bg-white dark:bg-dark-surface rounded-lg border border-gray-200 dark:border-dark-border p-6 space-y-5">
        <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">
          Data Source
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Redshift Connector
            </label>
            <select
              value={connectorId}
              onChange={event => setConnectorId(event.target.value)}
              className="w-full rounded-md border border-gray-300 dark:border-dark-border px-3 py-2 text-sm bg-white dark:bg-dark-surface text-gray-900 dark:text-gray-100 focus:outline-none"
            >
              <option value="">Select a connector</option>
              {connectors.map(connector => (
                <option key={connector.connector_id} value={connector.connector_id}>
                  {connector.connector_name} ({connector.connector_type})
                </option>
              ))}
            </select>
            {config?.connector_name && (
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                Current: {config.connector_name}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Redshift Schema
            </label>
            <input
              type="text"
              value={schema}
              onChange={event => setSchema(event.target.value)}
              placeholder="cashflow"
              className="w-full rounded-md border border-gray-300 dark:border-dark-border px-3 py-2 text-sm bg-white dark:bg-dark-surface text-gray-900 dark:text-gray-100 focus:outline-none"
            />
            {config?.redshift_schema && (
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                Current: {config.redshift_schema}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3 pt-2 flex-wrap">
          <button
            onClick={() =>
              connectorId &&
              setDataSource.mutate({
                connector_id: connectorId,
                redshift_schema: schema || 'cashflow',
              })
            }
            disabled={!connectorId || setDataSource.isPending}
            className="px-4 py-2 bg-teal-600 text-white text-sm font-medium rounded-md hover:bg-teal-700 disabled:opacity-50"
          >
            {setDataSource.isPending ? 'Saving...' : 'Save Configuration'}
          </button>
          <button
            onClick={() =>
              connectorId &&
              testConnection.mutate({
                connector_id: connectorId,
                redshift_schema: schema || 'cashflow',
              })
            }
            disabled={!connectorId || testConnection.isPending}
            className="px-4 py-2 bg-gray-100 dark:bg-dark-surface-2 text-gray-700 dark:text-gray-200 text-sm font-medium rounded-md hover:bg-gray-200 dark:hover:bg-dark-border disabled:opacity-50"
          >
            {testConnection.isPending ? 'Testing...' : 'Test Connection'}
          </button>

          {config?.mode === 'saved' && (
            <button
              onClick={() => {
                if (
                  window.confirm(
                    'Clear the saved connector? The applet will fall back to the environment (dev) Redshift connection if one is configured, otherwise pages will show the setup CTA.',
                  )
                ) {
                  clearDataSource.mutate()
                }
              }}
              disabled={clearDataSource.isPending}
              className="px-4 py-2 border border-red-300 text-red-700 dark:text-red-300 dark:border-red-900/60 text-sm font-medium rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50"
            >
              {clearDataSource.isPending ? 'Clearing…' : 'Revert to env fallback'}
            </button>
          )}

          {setDataSource.isSuccess && (
            <span className="text-sm text-emerald-600">Saved</span>
          )}
          {setDataSource.isError && (
            <span className="text-sm text-red-600">
              {setDataSource.error.message || 'Save failed'}
            </span>
          )}
          {clearDataSource.isSuccess && (
            <span className="text-sm text-emerald-600">Reverted to env fallback</span>
          )}
          {clearDataSource.isError && (
            <span className="text-sm text-red-600">
              {clearDataSource.error.message || 'Clear failed'}
            </span>
          )}
        </div>

        {testConnection.data && (
          <div
            className={`rounded-md p-3 text-sm border ${
              testConnection.data.redshift_connected
                ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                : 'bg-red-50 text-red-800 border-red-200'
            }`}
          >
            <strong>
              {testConnection.data.redshift_connected ? 'Connected' : 'Failed'}:
            </strong>{' '}
            {testConnection.data.message}
          </div>
        )}
        {testConnection.isError && (
          <div className="rounded-md p-3 text-sm border bg-red-50 text-red-800 border-red-200">
            <strong>Failed:</strong> {testConnection.error.message}
          </div>
        )}
      </div>

      <div className="bg-white dark:bg-dark-surface rounded-lg border border-gray-200 dark:border-dark-border p-6">
        <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-3">
          Active Connection
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
          <div>
            <dt className="text-gray-500 dark:text-gray-400">Connector</dt>
            <dd className="font-medium text-gray-900 dark:text-gray-100">
              {config?.connector_name || activeConnector?.connector_name || '—'}
            </dd>
          </div>
          <div>
            <dt className="text-gray-500 dark:text-gray-400">Mode</dt>
            <dd className="flex items-center gap-2">
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                  config?.mode === 'saved'
                    ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'
                    : config?.mode === 'env_fallback'
                      ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
                      : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                }`}
              >
                {config?.mode ?? 'unknown'}
              </span>
            </dd>
          </div>
          <div>
            <dt className="text-gray-500 dark:text-gray-400">Schema</dt>
            <dd className="font-medium text-gray-900 dark:text-gray-100">
              {config?.redshift_schema || schema}
            </dd>
          </div>
          <div>
            <dt className="text-gray-500 dark:text-gray-400">Table Prefix</dt>
            <dd className="font-medium text-gray-900 dark:text-gray-100">v2_*</dd>
          </div>
        </div>
        {config?.host ? (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
            Host: <span className="font-mono">{config.host}</span>
          </p>
        ) : null}
      </div>

      <div className="bg-gray-50 dark:bg-dark-surface-2 rounded-lg border border-gray-200 dark:border-dark-border p-6 text-sm text-gray-600 dark:text-gray-300 space-y-2">
        <h3 className="font-medium text-gray-900 dark:text-gray-100">Setup Notes</h3>
        <ul className="list-disc list-inside space-y-1">
          <li>Create a Redshift connector in `Data Connections` first, then select it here.</li>
          <li>The V2 applet expects `v2_*` tables inside the chosen schema.</li>
          <li>Use `scripts/seed_cashflow_v2_redshift.py` to create those tables and load fixture data.</li>
          <li>If your Redshift role cannot write to `cashflow`, seed `public` instead and set the schema here to `public`.</li>
          <li>Test the connection before opening other V2 routes.</li>
        </ul>
      </div>
    </div>
  )
}
